import React from 'react';
import PropTypes from 'prop-types';

import { useHistory } from 'react-router-dom';
import { toast, Status, Icon, ContextMenu, Button, DropdownItem, LinkWithActions, Tooltip } from '@weezevent/nacre';
import { useTranslation, Trans } from 'react-i18next';
import { useSegment } from '../../tracking/trackingProvider';

import ConfirmModal from '../../confirmModal';
import { ScheduleModal, CancelScheduleModal } from './event/schedule-modal';
import { ScheduledEventPublication } from './event/scheduled-event-publication';

import Style from './style/context-menu.module.css';
import OrganizationInfos from '../../../models/organizationInfos';
import { SendMailAccountActivationModal } from './event/sendMailAccountActivationModal';
import { OrganizationMustBeConfirmedModal } from './event/organizationMustBeConfirmedModal';
import i18n from 'i18next';
import Minisite from '../../../models/minisite';
import useAppContext from '../../../context/useAppContext';
import { capitalizeFirstLetter } from '../../../pages/attendee/detail/components/utils';
import { mappingColorStatus } from '../../../pages/widgets/eventListModal';

export function getLocalizationTypeEvent(event) {
    let type = 'location';

    if (['online_donations', 'memberships'].includes(event.subtype) && !event.address?.address1?.length && !event.website) {
        type = 'no_location';
    }
    if (!event.address?.address1?.length && event.website) {
        type = 'online';
    }
    if (type === 'no_location') {
        return 'None';
    }
    return capitalizeFirstLetter(type);
}

function EventContextCta({ event, onClick, loading = false }) {
    const { t } = useTranslation();

    if (event.isPublicationScheduled()) {
        return <Button label={t('context_menu.edit_schedule')} onClick={() => onClick('schedule')} disabled={loading} />;
    }

    if ((event.publication_status === 'published' || event.publication_status === 'draft') && !event.isOver()) {
        if (event.publication_status === 'draft') {
            return <Button primary label={t('ticket.common.action.publish')} onClick={() => onClick('published')} disabled={loading} />;
        } else {
            return <Button label={t('context_menu.draft')} onClick={() => onClick('draft')} disabled={loading} />;
        }
    }
    if (['published', 'draft'].includes(event.publication_status) && event.isOver()) {
        return <Button label={t('context_menu.close')} onClick={() => onClick('closed')} disabled={loading} />;
    }
    return (
        <Tooltip className={Style['tooltip-cta']} triggerOnHover title={t('context_menu.tooltip.button_reactivation')} margin={false} position={'top-start'}>
            <Button label={t('ticket.common.action.reactivate')} disabled />
        </Tooltip>
    );
}
EventContextCta.propTypes = {
    event: PropTypes.object.isRequired,
    onClick: PropTypes.func,
    loading: PropTypes.bool
};

function EventContextDropdownItems({ event, loading = false, onUpdateStatus, canPublish = false }) {
    const { t } = useTranslation();

    const items = [];

    const schedule_button = (
        <DropdownItem
            key="schedule"
            disabled={loading || !canPublish}
            isLoading={loading}
            onClick={() => {
                onUpdateStatus('schedule');
            }}
        >
            {t('context_menu.schedule')}
        </DropdownItem>
    );

    if (!event.isOver() && event.publication_status === 'draft' && !event.isPublicationScheduled()) {
        items.push(schedule_button);
    }

    if (event.isPublicationScheduled()) {
        items.push(
            <DropdownItem key="close" disabled={loading} isLoading={loading} onClick={() => onUpdateStatus('cancel_schedule')}>
                {t('context_menu.cancel_scheduling')}
            </DropdownItem>
        );
    }

    if (event.publication_status === 'draft' || (event.publication_status === 'published' && !event.isOver())) {
        items.push(
            <DropdownItem key="close" disabled={loading} isLoading={loading} onClick={() => onUpdateStatus('closed')}>
                {t('context_menu.close')}
            </DropdownItem>
        );

        if (event.isOver()) {
            items.push(
                <DropdownItem key="publish" disabled={loading} isLoading={loading} onClick={() => onUpdateStatus('published')}>
                    {t('ticket.common.action.publish')}
                </DropdownItem>
            );
        }
    }

    if (event.publication_status === 'published' && event.isOver()) {
        items.push(
            <DropdownItem key="unpublish" disabled={loading} isLoading={loading} onClick={() => onUpdateStatus('draft')}>
                {t('context_menu.draft')}
            </DropdownItem>
        );
    }

    return items.length ? items : null;
}

const getLocalizedDate = ({ date, locale, timezone }) => {
    return new Date(date).toLocaleDateString(locale, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZone: timezone
    });
};

const defaultTooltipProps = {
    margin: false,
    triggerOnHover: true,
    position: 'top-start'
};

function StatusContainer({ event }) {
    const { t } = useTranslation();
    return (
        <div style={{ display: 'flex' }}>
            <Status color={mappingColorStatus(event.publication_status)} text={t(`eventStatus.${event.publication_status}`)} />
            {event.isPublicationScheduled() && (
                <Status color="purple">
                    <Icon name="history" margin={false} />
                </Status>
            )}
        </div>
    );
}

function LinkMinisite({ event, organization, history, loading, minisite }) {
    const { t } = useTranslation();
    const disabledLink = React.useMemo(() => {
        if (!event.site && !event.gemino_url && event.page_url) {
            return false;
        }

        if (event.site && minisite?.is_published) {
            return false;
        }
        return true;
    }, [event, minisite]);

    return (
        <LinkWithActions
            disabled={disabledLink}
            className={Style['link-minisite']}
            url={disabledLink ? t('ticket.event.minisite.header.tooltipDisabled') : event.gemino_url || event.page_url}
            label={t('context_menu.link_minisite.label')}
            onEdit={() => history.push(`/ticket/O${organization.pk}/E${event.pk}/channels/page`)}
            position={'top-start'}
            tooltipCopied={t('context_menu.link_minisite.copied')}
            tooltipCopy={t('context_menu.link_minisite.copy')}
            tooltipEdit={t('context_menu.link_minisite.edit')}
        />
    );
}

export default function EventContextMenu({ organization, event }) {
    const history = useHistory();
    const { track } = useSegment();
    const { t } = useTranslation();
    const [{ reloadEvent: reloadEventContext }] = useAppContext();
    const locale = i18n.language;
    const [loading, setLoading] = React.useState(false);

    const [confirmStatus, setStatus] = React.useState(null);
    const [scheduling, setScheduling] = React.useState(false);
    const [cancelingSchedule, setCancelingSchedule] = React.useState(false);
    const [sendMailAccountActivationModal, setSendMailAccountActivationModal] = React.useState(false);
    const [organizationMustBeConfirmedModal, setOrganizationMustBeConfirmedModal] = React.useState(false);
    const [organizationInfos, loadingOrganizationInfos] = OrganizationInfos.useApiModel({
        organizationId: organization.id
    });

    const [minisite, isLoadingMinisite] = Minisite.useApiModel(
        {
            cache: false,
            organizationId: organization.id,
            id: event?.site,
            launch: Boolean(event?.site && event?.gemino_url)
        },
        [event, organization]
    );

    const eventDates = React.useMemo(() => {
        let _dates = [];
        if (event.start_date) {
            _dates.push(`${t('ticket.overview.dividerFrom')} ${getLocalizedDate({ date: event.start_date, locale, timezone: event.timezone })}`);
        }

        if (event.end_date) {
            _dates.push(`${t('ticket.overview.dividerTo')} ${getLocalizedDate({ date: event.end_date, locale, timezone: event.timezone })}`);
        }

        return _dates;
    }, [event, locale]);

    const canPublish = React.useCallback(() => {
        if (!organizationInfos) {
            return false;
        }

        if (organizationInfos.can_publish) {
            return true;
        }

        if (organizationInfos.can_publish_errors.includes('account_not_validated')) {
            setSendMailAccountActivationModal(true);
            return false;
        }

        // This case of organization not validated, will only occur if the owner has not validated his account,
        // because the validation of the account (Account), automatically validates the organization
        if (!organizationInfos.is_owner && organizationInfos.can_publish_errors.includes('organization_not_validated')) {
            setOrganizationMustBeConfirmedModal(true);
            return false;
        }

        return false;
    }, [organizationInfos]);

    const handlePublishTrack = React.useCallback(
        (is_scheduled = false) => {
            let values = {
                'Page Title': 'HOME EVENT',
                'Event Name': event.name,
                'Event Start Date': event.start_date,
                'Event End Date': event.end_date,
                'Event Type name': event.subtype,
                'Event Published Count': 1
            };

            track(`ACTION > ${is_scheduled ? ' SCHEDULE PUBLISH EVENT' : 'PUBLISH EVENT MENU'}`, values, {
                'Event Published List': [event.id],
                'Last Event Published Date': new Date().toUTCString()
            });
        },
        [track, event]
    );

    const handleCloseTrack = React.useCallback(() => {
        let values = {
            'Page Title': 'HOME EVENT',
            'Event Name': event.name,
            'Event Start Date': event.start_date,
            'Event End Date': event.end_date,
            'Event Type name': event.subtype,
            'Place Type': getLocalizationTypeEvent(event)
        };

        track(`WEEZTICKET > ACTION > CLOSE EVENT`, values);
    }, [track, event, getLocalizationTypeEvent]);

    const saveStatusEvent = React.useCallback(
        status => {
            // Maybe do not edit the props, instanciate a new model,
            // save in db using it then store the answer in context after instanciating a new model
            // but too lazy right now
            if (status === 'published') {
                handlePublishTrack(false);
            } else if (status === 'closed') {
                handleCloseTrack();
            }
            event.publication_status = status;
            event
                .save({
                    organizationId: organization.pk
                })
                .then(() => {
                    history.push(`/ticket/O${organization.id}/E${event.id}/dashboard`);
                    toast.success(t(`ticket.event.editSuccess`));
                })
                .catch(e => {
                    toast.error(t(`ticket.event.editError`));
                })
                .finally(() => {
                    setLoading(false);
                    reloadEventContext(false);
                });
        },
        [event, organization, reloadEventContext, handlePublishTrack, handleCloseTrack]
    );

    const updateStatus = React.useCallback(
        status => {
            setStatus(null);
            setLoading(true);
            if (!organizationInfos?.can_publish && status === 'published' && !canPublish()) {
                setLoading(false);
                toast.warning(t('ticket.creationEvent.toast.cannotPublish'));
            } else {
                saveStatusEvent(status);
            }
        },
        [event, history, organizationInfos, organization]
    );

    const handleStatus = React.useCallback(
        status => {
            // Status where we need a confirmation
            if (status === 'schedule') {
                handlePublishTrack(true);
                return setScheduling(true);
            }

            if (status === 'cancel_schedule') {
                return setCancelingSchedule(true);
            }

            if (['closed'].includes(status)) {
                return setStatus(status);
            }
            // if confirmation is not need, just do it
            return updateStatus(status);
        },
        [updateStatus, handlePublishTrack]
    );

    return (
        <>
            {Boolean(confirmStatus) && (
                <ConfirmModal
                    labelButton={t('context_menu.modal.button')}
                    labelTitle={t('context_menu.modal.title')}
                    labelBody={<Trans i18nKey="context_menu.modal.body" />}
                    objectToSave={event}
                    onClose={() => setStatus(null)}
                    onAccept={() => updateStatus(confirmStatus)}
                />
            )}
            <SendMailAccountActivationModal open={sendMailAccountActivationModal} onSubmit={() => {}} onClose={() => setSendMailAccountActivationModal(false)} />
            <OrganizationMustBeConfirmedModal open={organizationMustBeConfirmedModal} onClose={() => setOrganizationMustBeConfirmedModal(false)} />
            <ScheduleModal open={scheduling} onClose={() => setScheduling(false)} />
            <CancelScheduleModal open={cancelingSchedule} onClose={() => setCancelingSchedule(false)} />
            <ContextMenu
                catchphrase={
                    <span>
                        {`${t('context_menu.catchphrase_event')} `}
                        <span className={Style['event-catchphrase__id']}>{`${event.id}`}</span>
                    </span>
                }
                title={event.name}
                titleTooltipProps={{ ...defaultTooltipProps, title: event.name, position: 'bottom-start', offset: [0, -5] }}
                status={<StatusContainer event={event} />}
                subtitle={eventDates}
                subtitleTooltipProps={{ ...defaultTooltipProps, title: t(`eventList.${event.get_time_status()}`) }}
                linkComponent={<LinkMinisite organization={organization} event={event} history={history} minisite={minisite} loading={isLoadingMinisite} />}
                info={<ScheduledEventPublication event={event} />}
                cta={<EventContextCta event={event} onClick={handleStatus} loading={loading || loadingOrganizationInfos} />}
                dropdownProps={{ disabled: event.status === 'closed' }}
                className={Style['event-context-menu']}
                dropdownItems={[
                    <EventContextDropdownItems key={0} event={event} canPublish={organizationInfos?.can_publish || false} onUpdateStatus={handleStatus} loading={loading} />
                ]}
            />
        </>
    );
}

EventContextMenu.propTypes = {
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired
};
